import { useEffect, useState } from 'react';
import { resolveAddressLocale, type AddressLocale } from './addressTranslations';

export type PhoneInputI18n = Record<string, string>;

// The exports map types intl-tel-input/i18n/* as the main module, so the
// actual { default: Record<string, string> } shape needs asserting here.
type I18nModule = { default: PhoneInputI18n };

const loaders: Partial<Record<AddressLocale, () => Promise<unknown>>> = {
  de: () => import('intl-tel-input/i18n/de'),
  es: () => import('intl-tel-input/i18n/es'),
  fr: () => import('intl-tel-input/i18n/fr'),
  it: () => import('intl-tel-input/i18n/it'),
  nl: () => import('intl-tel-input/i18n/nl'),
  pl: () => import('intl-tel-input/i18n/pl'),
  'pt-BR': () => import('intl-tel-input/i18n/pt'),
  'pt-PT': () => import('intl-tel-input/i18n/pt'),
};

export async function loadPhoneInputI18n(locale?: string): Promise<PhoneInputI18n | undefined> {
  const loader = loaders[resolveAddressLocale(locale)];
  if (!loader) return undefined;
  const mod = (await loader()) as I18nModule;
  return mod.default;
}

/**
 * Resolves intl-tel-input's translation dictionary (country names + dropdown UI
 * strings) for the checkout locale. `ready` is true synchronously for English,
 * so callers can gate rendering on it without changing the English path.
 */
export function usePhoneInputI18n(locale?: string): {
  i18n: PhoneInputI18n | undefined;
  ready: boolean;
} {
  const resolved = resolveAddressLocale(locale);
  const needsI18n = Boolean(loaders[resolved]);
  const [loaded, setLoaded] = useState<{ locale: AddressLocale; i18n: PhoneInputI18n } | null>(
    null,
  );

  useEffect(() => {
    if (!needsI18n) return;
    let cancelled = false;
    loadPhoneInputI18n(resolved).then(i18n => {
      if (!cancelled && i18n) setLoaded({ locale: resolved, i18n });
    });
    return () => {
      cancelled = true;
    };
  }, [resolved, needsI18n]);

  if (!needsI18n) return { i18n: undefined, ready: true };
  const isCurrent = loaded?.locale === resolved;
  return { i18n: isCurrent ? loaded.i18n : undefined, ready: isCurrent };
}
